import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {Subject} from 'rxjs';
import {DetectMethodChanges, DetectSetterChanges} from '../utils';
import {takeUntil} from 'rxjs/operators';
import {TradingInstrument} from '../trading-instruments/trading-instrument.class';
import {TradingInstrumentKind} from '../trading-instruments/trading-instrument-kind.enum';
import {LastQuoteCacheService} from '../last-quote-cache.service';
import {MessageBusService} from '../message-bus.service';
import {OptionsChainService} from '../option-chains.service';
import {OptionExpirationDescriptor} from '../shell-communication/shell-dto-protocol';
import {QuoteDto} from '../shell-communication/dtos/quote-dto.class';
import {makeOptionTicker, OptionType} from '../options-common/options.model';
import {optionPrice} from './pha-sheet.model';

export interface PhaItmCallSelection {
    instrument: TradingInstrument;
    expiration: OptionExpirationDescriptor;
    strike: number;
    contracts: number;
    optionTicker: string;
}

/** ITM is an entry gate only. The chosen call remains on the sheet if spot later moves below strike. */
@Component({
    selector: 'ets-pha-add-itm-call-dialog',
    templateUrl: './pha-add-itm-call-dialog.component.html',
    styleUrls: ['./pha-add-itm-call-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PhaAddItmCallDialogComponent implements OnInit, OnDestroy {
    constructor(private readonly _changeDetector: ChangeDetectorRef,
                private readonly _quotes: LastQuoteCacheService,
                private readonly _bus: MessageBusService,
                private readonly _chains: OptionsChainService) {}

    @Output() callAdded = new EventEmitter<PhaItmCallSelection>();
    visible = false;
    busy = false;
    instrument: TradingInstrument = null;
    expirations: OptionExpirationDescriptor[] = [];
    expiration: OptionExpirationDescriptor = null;
    strike: number = null;
    private _contracts = 1;
    get contracts(): number { return this._contracts; }
    @DetectSetterChanges()
    set contracts(value: number) { this._contracts = value; }
    lastPrice: number = null;
    callPrice: number = null;
    private _optionTicker: string = null;
    chainError = '';
    readonly centered = {style: 'text-align: center;'};
    private readonly _unsub = new Subject<void>();
    private _subscribedTicker: string = null;
    private _generation = 0;
    private _destroyed = false;

    ngOnInit(): void {
        this._bus.of<QuoteDto[]>('QuoteDto').pipe(takeUntil(this._unsub)).subscribe(msg => {
            if (!this.visible || !this.instrument) { return; }
            const quote = (msg.payload || []).find(q => q.ticker === this.instrument.ticker);
            if (quote) { this.setQuote(quote); }
            const callQuote = this._optionTicker && (msg.payload || []).find(q => q.ticker === this._optionTicker);
            if (callQuote) { this.setCallQuote(callQuote); }
        });
    }

    @DetectMethodChanges()
    open(): void {
        this.close();
        this.instrument = null;
        this.expirations = [];
        this.expiration = null;
        this.strike = null;
        this.contracts = 1;
        this.lastPrice = null;
        this.chainError = '';
        this.visible = true;
    }

    @DetectMethodChanges()
    close(): void {
        ++this._generation;
        this.clearCallQuote();
        this.visible = false;
        this.busy = false;
        if (this._subscribedTicker) {
            this._quotes.unsubscribeTicker(this._subscribedTicker);
            this._subscribedTicker = null;
        }
    }

    @DetectMethodChanges({isAsync: true})
    async onInstrumentSelected(instrument: TradingInstrument): Promise<void> {
        const generation = ++this._generation;
        this.clearCallQuote();
        if (this._subscribedTicker) { this._quotes.unsubscribeTicker(this._subscribedTicker); }
        this._subscribedTicker = null;
        this.instrument = instrument;
        this.expirations = [];
        this.expiration = null;
        this.strike = null;
        this.lastPrice = null;
        this.chainError = '';
        this.busy = false;
        if (!instrument) { return; }
        if (instrument.kind === TradingInstrumentKind.Option) {
            this.chainError = 'Select The Underlying Symbol First.';
            return;
        }
        this._subscribedTicker = instrument.ticker;
        this.setQuote(this._quotes.subscribeTicker(instrument.ticker));
        this.busy = true;
        // Render the reset/loading state before awaiting the chain. The async decorator
        // renders the completed chain even when the reply arrives outside Angular's zone.
        this._changeDetector.detectChanges();
        try {
            const chain = await this._chains.getChain(instrument.ticker);
            if (generation !== this._generation || this._destroyed || !this.visible) { return; }
            this.expirations = (chain && chain.expirations || []).filter(e => e.strikes && e.strikes.length
                && !this._chains.isExpirationExpired(e.optionExpirationDate));
            if (!this.expirations.length) { this.chainError = 'No Available Call Expirations For This Symbol.'; }
        } catch (error) {
            if (generation !== this._generation || this._destroyed || !this.visible) { return; }
            this.chainError = 'The Option Chain Could Not be Loaded. Select The Symbol Again to Retry.';
            console.error('[pha] ITM call chain failed', error);
        } finally {
            if (generation === this._generation && !this._destroyed) {
                this.busy = false;
            }
        }
    }

    @DetectMethodChanges()
    onExpirationChanged(ev: {value: OptionExpirationDescriptor}): void {
        this.expiration = ev.value || null;
        this.strike = null;
        this.clearCallQuote();
    }

    @DetectMethodChanges()
    onStrikeChanged(strike: number): void {
        this.strike = strike;
        this.subscribeCallQuote();
    }

    get itmPercent(): number | null {
        return this.lastPrice > 0 && typeof this.strike === 'number' && isFinite(this.strike)
            ? (this.strike - this.lastPrice) / this.lastPrice * 100 : null;
    }

    get validationMessage(): string {
        if (this.chainError) { return this.chainError; }
        if (!this.instrument || this.busy) { return ''; }
        if (!(this.lastPrice > 0)) { return 'Waiting For The Underlying’s Last Price.'; }
        if (!this.expiration || this.strike === null || this.strike === undefined) { return ''; }
        if (this._chains.isExpirationExpired(this.expiration.optionExpirationDate)) { return 'Select an Available Expiration.'; }
        if (this.expiration.strikes.indexOf(this.strike) < 0) { return 'Select a Listed Strike.'; }
        if (!(this.strike > 0 && this.strike < this.lastPrice)) { return 'Only ITM Calls Can be Added. Select a Strike Below The Last Price.'; }
        if (!Number.isInteger(this.contracts) || this.contracts < 1) { return 'Enter a Positive Whole Number of Contracts.'; }
        return '';
    }

    get canAdd(): boolean {
        return this.visible && !this.busy && !!this.instrument && !!this.expiration
            && this.expirations.indexOf(this.expiration) >= 0 && this.strike > 0
            && this.lastPrice > this.strike && Number.isInteger(this.contracts) && this.contracts > 0
            && !this.validationMessage;
    }

    @DetectMethodChanges()
    add(): void {
        // Re-read at click time: a price tick can turn an ITM strike into ATM/OTM after selection.
        this.setQuote(this.instrument && this._quotes.getLastQuote(this.instrument.ticker));
        if (!this.canAdd) { return; }
        const optionTicker = makeOptionTicker(this.expiration, OptionType.Call, this.strike);
        if (!optionTicker) { return; }
        this.callAdded.emit({instrument: this.instrument, expiration: this.expiration,
            strike: this.strike, contracts: this.contracts, optionTicker});
    }

    matches(selection: PhaItmCallSelection): boolean {
        return this.canAdd && this.instrument === selection.instrument && this.expiration === selection.expiration
            && this.strike === selection.strike && this.contracts === selection.contracts;
    }

    private clearCallQuote(): void {
        const ticker = this._optionTicker;
        this._optionTicker = null;
        this.callPrice = null;
        if (ticker) { this._quotes.unsubscribeTicker(ticker); }
    }

    private subscribeCallQuote(): void {
        const ticker = this.expiration && this.strike > 0
            && this.expiration.strikes.indexOf(this.strike) >= 0
            ? makeOptionTicker(this.expiration, OptionType.Call, this.strike) : null;
        if (ticker === this._optionTicker) { return; }
        this.clearCallQuote();
        if (!ticker || !this.visible) { return; }
        this._optionTicker = ticker;
        this.setCallQuote(this._quotes.subscribeTicker(ticker));
    }

    @DetectMethodChanges()
    private setCallQuote(quote: QuoteDto): void {
        const finite = (value: number) => typeof value === 'number' && isFinite(value) ? value : null;
        this.callPrice = quote && quote.ticker === this._optionTicker
            ? optionPrice(finite(quote.bid), finite(quote.ask), finite(quote.mid), finite(quote.lastPx)) : null;
    }

    @DetectMethodChanges()
    private setQuote(quote: QuoteDto): void {
        this.lastPrice = quote && typeof quote.lastPx === 'number' && isFinite(quote.lastPx) && quote.lastPx > 0 ? quote.lastPx : null;
    }

    ngOnDestroy(): void {
        this._destroyed = true;
        this.close();
        this._unsub.next();
        this._unsub.complete();
    }
}
